import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Search, X, Loader2, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

interface SelectItem {
  id: string;
  name: string;
}

interface SelectTagInputWithCheckboxesProps {
  value: string[];
  onChange: (_ids: string[]) => void;
  placeholder?: string;
  className?: string;
  showSearchIcon?: boolean;
  items: SelectItem[];
  loading?: boolean;
  onSearch?: (_query: string) => void;
  required?: boolean;
  buttonLabel?: string;
}

export default function SelectTagInputWithCheckboxes({ 
  value = [], 
  onChange, 
  placeholder = 'Pesquisar',
  className = '',
  showSearchIcon = true,
  items = [],
  loading = false,
  onSearch,
  buttonLabel = 'Adicionar'
}: SelectTagInputWithCheckboxesProps) {
  const [inputValue, setInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isUpdatingRef = useRef(false);

  const filteredItems = useMemo(() => {
    if (inputValue.trim()) {
      return items.filter(item => 
        item.name.toLowerCase().includes(inputValue.toLowerCase())
      );
    }
    return items;
  }, [inputValue, items]);

  const selectedItems = useMemo(() => {
    return value.map(id => items.find(item => item.id === id)).filter(Boolean) as SelectItem[];
  }, [value, items]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setInputValue('');
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);

  useEffect(() => {
    if (showDropdown && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showDropdown]);

  const handleToggleItem = useCallback((itemId: string) => {
    if (isUpdatingRef.current) return;
    
    isUpdatingRef.current = true;
    const isCurrentlySelected = value.includes(itemId);
    if (isCurrentlySelected) {
      onChange(value.filter(id => id !== itemId));
    } else {
      onChange([...value, itemId]);
    }
    
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 0);
  }, [value, onChange]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setInputValue(query);
    onSearch?.(query);
  }, [onSearch]);

  const handleRemoveTag = useCallback((itemId: string) => {
    onChange(value.filter(id => id !== itemId));
  }, [value, onChange]);

  const handleOpenDropdown = useCallback(() => {
    setShowDropdown(true);
  }, []);

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} ref={containerRef}>
      {selectedItems.map((item) => (
        <div 
          key={item.id} 
          className="flex items-center gap-1 bg-gray-800 text-white px-3 py-1 rounded-full text-sm font-medium"
        >
          <span>{item.name}</span>
          <button
            type="button"
            onClick={() => handleRemoveTag(item.id)}
            className="text-white hover:text-gray-300 transition-colors"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
      
      <div className="relative">
        <Button
          ref={buttonRef}
          type="button"
          variant="outline"
          onClick={handleOpenDropdown}
          className="border-gray-200 hover:bg-gray-50 px-3 py-1 h-auto"
        >
          <div className="flex items-center gap-1.5">
            <Plus className="w-4 h-4" />
            <span className="text-sm font-medium">{buttonLabel}</span>
          </div>
        </Button>
        
        {showDropdown && (
          <div className="absolute top-full left-0 bg-white border border-gray-200 rounded-md shadow-lg z-30 mt-1 min-w-[200px]">
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                {showSearchIcon && (
                  <Search className="absolute left-3 top-1/2 transform z-20 -translate-y-1/2 text-gray-400 w-4 h-4" />
                )}
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={handleInputChange}
                  placeholder={placeholder}
                  className={`w-full ${showSearchIcon ? 'pl-10' : ''}`}
                />
              </div>
            </div>
            
            <div className="max-h-48 overflow-y-auto">
              {loading ? (
                <div className="flex items-center gap-2 px-3 py-3 text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Carregando...
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="px-3 py-3 text-gray-500 text-sm">
                  Nenhum item encontrado
                </div>
              ) : (
                filteredItems.map((item) => {
                  const isChecked = value.includes(item.id);
                  return (
                    <div
                      key={item.id}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => {
                          handleToggleItem(item.id);
                        }}
                      />
                      <span 
                        className="text-sm flex-1"
                        onClick={() => handleToggleItem(item.id)}
                      >
                        {item.name}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
